import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { QRCodeSVG } from "qrcode.react";
import { UserPlus, Trash2, Store, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { shops, refresh } = useShop();
  const [users, setUsers] = useState([]);
  const [editShop, setEditShop] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "staff", shop_access: ["shop1"] });
  const [newShopOpen, setNewShopOpen] = useState(false);
  const [newShop, setNewShop] = useState({ shop_id: "", name: "", address: "", phone: "", gstin: "", upi_id: "", allowed_categories: "" });
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState({ action: "all", user: "all" });

  const loadLogs = async () => {
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (logFilter.action !== "all") params.set("action", logFilter.action);
      if (logFilter.user !== "all") params.set("user_id", logFilter.user);
      const r = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(r.data || []);
    } catch { setLogs([]); }
  };

  const createShop = async () => {
    if (!newShop.name.trim()) { toast.error("Shop name required"); return; }
    try {
      const payload = {
        ...newShop,
        allowed_categories: (newShop.allowed_categories || "").split(",").map((x) => x.trim()).filter(Boolean),
      };
      if (!payload.shop_id) delete payload.shop_id;
      await api.post("/shops", payload);
      toast.success(`Shop "${newShop.name}" created`);
      setNewShopOpen(false);
      setNewShop({ shop_id: "", name: "", address: "", phone: "", gstin: "", upi_id: "", allowed_categories: "" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const loadUsers = async () => {
    try { const r = await api.get("/users"); setUsers(r.data); } catch {}
  };
  useEffect(()=>{ loadUsers(); }, []);

  React.useEffect(()=>{
    const m = {};
    shops.forEach((s)=> m[s.shop_id] = { ...s });
    setEditShop(m);
  }, [shops]);

  const saveShop = async (sid) => {
    try {
      await api.put(`/shops/${sid}`, editShop[sid]);
      toast.success("Saved");
      refresh();
    } catch { toast.error("Failed"); }
  };

  const updateUser = async (uid, patch) => {
    await api.put(`/users/${uid}`, patch);
    loadUsers();
    toast.success("Updated");
  };

  const toggleShopAccess = (user, shop_id) => {
    const cur = user.shop_access || [];
    const next = cur.includes(shop_id) ? cur.filter((x) => x !== shop_id) : [...cur, shop_id];
    updateUser(user.user_id, { shop_access: next });
  };

  const createUser = async () => {
    if (!newUser.email) return toast.error("Email required");
    if (!newUser.password || newUser.password.length < 4) return toast.error("Password min 4 chars");
    try {
      await api.post("/users", newUser);
      toast.success("User created — share password with them");
      setCreateOpen(false);
      setNewUser({ email: "", name: "", password: "", role: "staff", shop_access: ["shop1"] });
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteUser = async (u) => {
    if (!confirm(`Delete user ${u.email}? They will be logged out and removed.`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("User deleted");
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-8 overflow-y-auto" data-testid="settings-page">
      <div className="mb-6">
        <div className="label-overline text-muted-foreground">Configuration</div>
        <h1 className="font-heading text-4xl font-bold text-ink">Settings</h1>
      </div>

      <Tabs defaultValue="shops">
        <TabsList className="bg-surface-alt">
          <TabsTrigger value="shops" data-testid="tab-shops">Shops &amp; UPI</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users &amp; Roles</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Activity Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="shops" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setNewShopOpen(true)} data-testid="add-shop-btn" className="bg-terracotta hover:bg-terracotta-hover text-white gap-2">
              <Store size={14} /> Add Shop
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shops.map((s) => {
            const e = editShop[s.shop_id] || s;
            const upi = e.upi_id ? `upi://pay?pa=${e.upi_id}&pn=${encodeURIComponent(e.upi_payee_name||e.name)}&cu=INR` : "";
            return (
              <Card key={s.shop_id} className="border-border shadow-none" data-testid={`shop-card-${s.shop_id}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="font-heading text-xl font-bold">{s.name}</div>
                  <div>
                    <Label className="text-xs">Shop Name</Label>
                    <Input value={e.name||""} onChange={(ev)=>setEditShop({...editShop, [s.shop_id]:{...e, name:ev.target.value}})} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">GSTIN</Label><Input value={e.gstin||""} onChange={(ev)=>setEditShop({...editShop, [s.shop_id]:{...e, gstin:ev.target.value}})} /></div>
                    <div><Label className="text-xs">Phone</Label><Input value={e.phone||""} onChange={(ev)=>setEditShop({...editShop, [s.shop_id]:{...e, phone:ev.target.value}})} /></div>
                  </div>
                  <div><Label className="text-xs">Address</Label><Input value={e.address||""} onChange={(ev)=>setEditShop({...editShop, [s.shop_id]:{...e, address:ev.target.value}})} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">UPI ID</Label><Input data-testid={`upi-id-${s.shop_id}`} placeholder="yourname@upi" value={e.upi_id||""} onChange={(ev)=>setEditShop({...editShop, [s.shop_id]:{...e, upi_id:ev.target.value}})} /></div>
                    <div><Label className="text-xs">Payee Name</Label><Input value={e.upi_payee_name||""} onChange={(ev)=>setEditShop({...editShop, [s.shop_id]:{...e, upi_payee_name:ev.target.value}})} /></div>
                  </div>
                  <div>
                    <Label className="text-xs">Allowed Categories (comma-sep, empty = all)</Label>
                    <Input
                      data-testid={`allowed-cat-${s.shop_id}`}
                      placeholder="electronics, watches"
                      value={(Array.isArray(e.allowed_categories) ? e.allowed_categories.join(", ") : (e.allowed_categories || ""))}
                      onChange={(ev)=>setEditShop({
                        ...editShop,
                        [s.shop_id]:{...e, allowed_categories: ev.target.value.split(",").map((x)=>x.trim()).filter(Boolean)}
                      })}
                    />
                  </div>
                  <div><Label className="text-xs">Terms &amp; Conditions</Label><Input value={e.terms||""} onChange={(ev)=>setEditShop({...editShop, [s.shop_id]:{...e, terms:ev.target.value}})} /></div>
                  {upi && (
                    <div className="flex items-center gap-3 bg-sand p-3 rounded">
                      <QRCodeSVG value={upi} size={70} />
                      <div className="text-xs">
                        <div className="label-overline text-muted-foreground">Auto-generated QR</div>
                        <div className="font-mono mt-1">{e.upi_id}</div>
                      </div>
                    </div>
                  )}
                  <Button onClick={() => saveShop(s.shop_id)} data-testid={`save-shop-${s.shop_id}`} className="w-full bg-terracotta hover:bg-terracotta-hover text-white">Save</Button>
                </CardContent>
              </Card>
            );
          })}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setCreateOpen(true)} data-testid="create-user-btn" className="bg-terracotta hover:bg-terracotta-hover text-white gap-2">
              <UserPlus size={14} /> Create Staff
            </Button>
          </div>
          <Card className="border-border shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-surface-alt">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Shop Access</TableHead>
                    <TableHead>Allow Neg. Stock</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u)=>(
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-xs">{u.email}</TableCell>
                      <TableCell>
                        <Select value={u.role} onValueChange={(v)=>updateUser(u.user_id, {role: v})}>
                          <SelectTrigger className="w-32" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-3">
                          {shops.map((s) => {
                            const checked = (u.shop_access || []).includes(s.shop_id);
                            return (
                              <label key={s.shop_id} className="flex items-center gap-1 text-xs cursor-pointer">
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={() => toggleShopAccess(u, s.shop_id)}
                                  data-testid={`shop-access-${u.user_id}-${s.shop_id}`}
                                />
                                {s.shop_id}
                              </label>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={!!u.allow_negative_stock}
                          onCheckedChange={(v) => updateUser(u.user_id, { allow_negative_stock: !!v })}
                          data-testid={`allow-neg-${u.user_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-xs">{u.created_at?.slice(0,10)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.user_id}`} className="text-destructive hover:bg-destructive/10">
                          <Trash2 size={14}/>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length===0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No users yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground mt-3">First Google login becomes admin. Use "Create Staff" to add new staff with specific shop access. Staff sees only their assigned shop(s).</p>
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <Card className="border-border shadow-none">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-muted-foreground"/>
                  <span className="font-heading font-bold">Activity Logs</span>
                </div>
                <select
                  className="h-8 px-2 border border-border rounded text-sm"
                  value={logFilter.action}
                  onChange={(e) => setLogFilter({ ...logFilter, action: e.target.value })}
                  data-testid="log-action-filter"
                >
                  <option value="all">All actions</option>
                  <option value="login">Login</option>
                  <option value="logout">Logout</option>
                  <option value="product.create">Product Created</option>
                  <option value="product.update">Product Updated</option>
                  <option value="product.delete">Product Deleted</option>
                  <option value="bill.create">Bill Created</option>
                  <option value="bill.refund">Bill Refunded</option>
                  <option value="user.create">User Created</option>
                  <option value="user.delete">User Deleted</option>
                  <option value="shop.create">Shop Created</option>
                  <option value="loyalty.redeem">Loyalty Redeemed</option>
                </select>
                <select
                  className="h-8 px-2 border border-border rounded text-sm"
                  value={logFilter.user}
                  onChange={(e) => setLogFilter({ ...logFilter, user: e.target.value })}
                  data-testid="log-user-filter"
                >
                  <option value="all">All users</option>
                  {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                </select>
                <Button size="sm" onClick={loadLogs} data-testid="load-logs-btn" className="bg-terracotta text-white h-8">Refresh</Button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto scroll-thin">
                <Table>
                  <TableHeader className="sticky top-0 bg-surface-alt">
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((l) => (
                      <TableRow key={l.log_id} data-testid={`log-row-${l.log_id}`}>
                        <TableCell className="font-mono text-xs">{new Date(l.timestamp).toLocaleString()}</TableCell>
                        <TableCell>{l.user_name || l.user_id || "system"}</TableCell>
                        <TableCell><Badge variant="outline" className="font-mono text-[10px]">{l.action}</Badge></TableCell>
                        <TableCell className="text-xs">{l.target_type}{l.target_id ? ` · ${l.target_id.slice(0, 8)}` : ""}</TableCell>
                        <TableCell className="text-xs max-w-md">
                          {l.details ? (typeof l.details === "string" ? l.details : JSON.stringify(l.details).slice(0, 160)) : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                    {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No logs. Click Refresh.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Shop Dialog */}
      <Dialog open={newShopOpen} onOpenChange={setNewShopOpen}>
        <DialogContent data-testid="add-shop-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add New Shop</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Shop ID (slug, optional)</Label><Input placeholder="auto-generated if empty" value={newShop.shop_id} onChange={(e)=>setNewShop({...newShop, shop_id:e.target.value})} data-testid="new-shop-id"/></div>
              <div><Label className="text-xs">Shop Name*</Label><Input value={newShop.name} onChange={(e)=>setNewShop({...newShop, name:e.target.value})} data-testid="new-shop-name"/></div>
            </div>
            <div><Label className="text-xs">Address</Label><Input value={newShop.address} onChange={(e)=>setNewShop({...newShop, address:e.target.value})} data-testid="new-shop-address"/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Phone</Label><Input value={newShop.phone} onChange={(e)=>setNewShop({...newShop, phone:e.target.value})}/></div>
              <div><Label className="text-xs">GSTIN</Label><Input value={newShop.gstin} onChange={(e)=>setNewShop({...newShop, gstin:e.target.value})}/></div>
            </div>
            <div><Label className="text-xs">UPI ID</Label><Input placeholder="shop@upi" value={newShop.upi_id} onChange={(e)=>setNewShop({...newShop, upi_id:e.target.value})}/></div>
            <div>
              <Label className="text-xs">Allowed Categories (comma-separated)</Label>
              <Input
                placeholder="e.g. electronics, watches (empty = all categories)"
                value={newShop.allowed_categories}
                onChange={(e)=>setNewShop({...newShop, allowed_categories:e.target.value})}
                data-testid="new-shop-categories"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Only products with these categories will be shown in this shop's POS.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setNewShopOpen(false)}>Cancel</Button>
            <Button onClick={createShop} data-testid="create-shop-confirm" className="bg-terracotta hover:bg-terracotta-hover text-white">Create Shop</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email *</Label>
              <Input data-testid="new-user-email" placeholder="staff@yourshop.com" value={newUser.email} onChange={(e)=>setNewUser({...newUser, email: e.target.value})} />
              <p className="text-[10px] text-muted-foreground mt-1">User must use this email + password to log in.</p>
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input data-testid="new-user-name" value={newUser.name} onChange={(e)=>setNewUser({...newUser, name: e.target.value})} />
            </div>
            <div>
              <Label className="text-xs">Password *</Label>
              <Input data-testid="new-user-password" type="text" placeholder="min 4 chars" value={newUser.password} onChange={(e)=>setNewUser({...newUser, password: e.target.value})} />
              <p className="text-[10px] text-muted-foreground mt-1">Share this password with the user.</p>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={newUser.role} onValueChange={(v)=>setNewUser({...newUser, role: v})}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (POS only)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Shop Access</Label>
              <div className="flex gap-4 mt-2">
                {shops.map((s) => {
                  const checked = newUser.shop_access.includes(s.shop_id);
                  return (
                    <label key={s.shop_id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => {
                          const cur = newUser.shop_access;
                          setNewUser({...newUser, shop_access: cur.includes(s.shop_id) ? cur.filter((x)=>x!==s.shop_id) : [...cur, s.shop_id]});
                        }}
                        data-testid={`new-user-shop-${s.shop_id}`}
                      />
                      <span className="text-sm">{s.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createUser} data-testid="new-user-save" className="bg-terracotta hover:bg-terracotta-hover text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
